import { useState } from 'react';
import {
  ArrowLeft,
  Play,
  RotateCcw,
  Check,
  Flag,
  Wifi,
  WifiOff,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';

type SetData = {
  setNumber: number;
  isTiebreak: boolean;
  homePoints: number;
  awayPoints: number;
  winnerTeamId: string | null;
  finishedAt: string | null;
};

export default function RefereeDemoPage() {
  const [currentSetNo, setCurrentSetNo] = useState(2);
  const [currentHome, setCurrentHome] = useState(18);
  const [currentAway, setCurrentAway] = useState(16);
  const [homeSets, setHomeSets] = useState(1);
  const [awaySets, setAwaySets] = useState(0);
  const [status, setStatus] = useState<'SCHEDULED' | 'LIVE' | 'FINISHED'>('LIVE');
  const [connected] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [sets, setSets] = useState<SetData[]>([
    {
      setNumber: 1,
      isTiebreak: false,
      homePoints: 25,
      awayPoints: 21,
      winnerTeamId: 'home',
      finishedAt: new Date().toISOString(),
    },
    {
      setNumber: 2,
      isTiebreak: false,
      homePoints: 18,
      awayPoints: 16,
      winnerTeamId: null,
      finishedAt: null,
    },
  ]);

  const homeName = 'Skra Bełchatów';
  const awayName = 'ZAKSA Kędzierzyn-Koźle';
  const canScore = status === 'LIVE';
  const canFinish = status === 'LIVE';

  const rules = {
    setTarget: 25,
    tiebreakTarget: 15,
    allowTiebreak: true,
  };

  const isCurrentSetTiebreak = rules.allowTiebreak && currentSetNo === 3;
  const currentSetTarget = isCurrentSetTiebreak ? rules.tiebreakTarget : rules.setTarget;
  const currentSetMaxPts = Math.max(currentHome, currentAway);
  const canEndSet = canScore && currentSetMaxPts >= currentSetTarget && currentHome !== currentAway;

  const title = `${homeName} vs ${awayName} • PLAYOFF`;

  const handleAddPointHome = () => {
    if (!canScore) return;
    setBusy(true);
    setTimeout(() => {
      setCurrentHome(currentHome + 1);
      setSets((prev) =>
        prev.map((s) =>
          s.setNumber === currentSetNo && !s.finishedAt
            ? { ...s, homePoints: currentHome + 1 }
            : s
        )
      );
      setBusy(false);
    }, 200);
  };

  const handleAddPointAway = () => {
    if (!canScore) return;
    setBusy(true);
    setTimeout(() => {
      setCurrentAway(currentAway + 1);
      setSets((prev) =>
        prev.map((s) =>
          s.setNumber === currentSetNo && !s.finishedAt
            ? { ...s, awayPoints: currentAway + 1 }
            : s
        )
      );
      setBusy(false);
    }, 200);
  };

  const handleUndo = () => {
    setBusy(true);
    setTimeout(() => {
      if (currentHome > currentAway && currentHome > 0) {
        setCurrentHome(currentHome - 1);
        setSets((prev) =>
          prev.map((s) =>
            s.setNumber === currentSetNo && !s.finishedAt
              ? { ...s, homePoints: currentHome - 1 }
              : s
          )
        );
      } else if (currentAway > 0) {
        setCurrentAway(currentAway - 1);
        setSets((prev) =>
          prev.map((s) =>
            s.setNumber === currentSetNo && !s.finishedAt
              ? { ...s, awayPoints: currentAway - 1 }
              : s
          )
        );
      }
      setBusy(false);
    }, 200);
  };

  const handleEndSet = () => {
    if (!canEndSet) {
      setErr(
        `Set musi być wygrany do co najmniej ${currentSetTarget} punktów (obecnie ${currentHome}:${currentAway}).`
      );
      return;
    }
    setBusy(true);
    setTimeout(() => {
      const winner = currentHome > currentAway ? 'home' : 'away';
      setSets((prev) =>
        prev.map((s) =>
          s.setNumber === currentSetNo
            ? { ...s, finishedAt: new Date().toISOString(), winnerTeamId: winner }
            : s
        )
      );

      if (winner === 'home') {
        setHomeSets(homeSets + 1);
      } else {
        setAwaySets(awaySets + 1);
      }

      setCurrentSetNo(currentSetNo + 1);
      setCurrentHome(0);
      setCurrentAway(0);
      setSets((prev) => [
        ...prev,
        {
          setNumber: currentSetNo + 1,
          isTiebreak: currentSetNo + 1 === 3,
          homePoints: 0,
          awayPoints: 0,
          winnerTeamId: null,
          finishedAt: null,
        },
      ]);
      setBusy(false);
    }, 500);
  };

  const handleFinishMatch = () => {
    setBusy(true);
    setTimeout(() => {
      setStatus('FINISHED');
      setBusy(false);
      setErr('✅ Mecz zakończony pomyślnie! (DEMO)');
    }, 500);
  };

  const handleWalkover = (winner: 'HOME' | 'AWAY') => {
    const reason = window.prompt('Powód walkowera (opcjonalnie):', '') ?? '';
    setBusy(true);
    setTimeout(() => {
      setStatus('FINISHED');
      setBusy(false);
      setErr(
        `Walkower dla ${winner === 'HOME' ? homeName : awayName}. Powód: ${reason || 'Brak'} (DEMO)`
      );
    }, 500);
  };

  const handleStart = () => {
    setBusy(true);
    setTimeout(() => {
      setStatus('LIVE');
      setBusy(false);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-4 p-4 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-xl text-center font-bold shadow-lg">
          🎮 TRYB DEMO – Panel sędziego Turnivo (funkcje działają lokalnie)
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#0066FF] to-[#00C7B1] bg-clip-text text-transparent mb-2">
                {title}
              </h1>
              <div className="flex flex-wrap gap-2 items-center">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    status === 'LIVE'
                      ? 'bg-green-100 text-green-700'
                      : status === 'FINISHED'
                        ? 'bg-slate-100 text-slate-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  Status: {status}
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                  Set: {currentSetNo}
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                  Sety: {homeSets} - {awaySets}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                    connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                  {connected ? 'Połączono (DEMO)' : 'Rozłączono'}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <button
                onClick={handleStart}
                disabled={busy || status === 'LIVE' || status === 'FINISHED'}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#00C7B1] to-[#00A896] text-white rounded-xl hover:shadow-lg hover:shadow-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm"
              >
                <Play className="w-4 h-4" />
                START MECZ
              </button>
              <button
                onClick={() => setErr(null)}
                disabled={busy}
                className="flex items-center gap-2 px-4 py-2 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-all font-bold text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Odśwież
              </button>
              <a
                href="/"
                className="flex items-center gap-2 px-4 py-2 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 transition-all font-bold text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                Wróć do strony głównej
              </a>
            </div>
          </div>
        </div>

        {err && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/40 backdrop-blur-sm"
            onClick={() => setErr(null)}
          >
            <div
              className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-4">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    err.includes('✅') ? 'bg-green-100' : 'bg-red-100'
                  }`}
                >
                  <AlertTriangle
                    className={`w-5 h-5 ${err.includes('✅') ? 'text-green-600' : 'text-red-600'}`}
                  />
                </div>
                <div>
                  <h3
                    className={`text-lg font-bold mb-2 ${
                      err.includes('✅') ? 'text-green-900' : 'text-red-900'
                    }`}
                  >
                    {err.includes('✅') ? 'Sukces' : 'Uwaga'}
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed">{err}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setErr(null)}
                  className="px-6 py-2 bg-gradient-to-r from-[#0066FF] to-[#00C7B1] text-white rounded-xl hover:shadow-lg transition-all font-bold"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 shadow-lg">
            <div className="text-xs font-bold text-green-700 uppercase mb-2">{homeName}</div>
            <div className="text-6xl font-black text-green-900 mb-4">{currentHome}</div>
            <button
              disabled={busy || !canScore}
              onClick={handleAddPointHome}
              title={!canScore ? 'Mecz zakończony' : `Dodaj punkt: ${homeName}`}
              className="w-full py-6 bg-gradient-to-r from-green-400 to-emerald-400 text-white rounded-xl font-black text-2xl hover:shadow-xl hover:shadow-green-500/30 transition-all transform hover:-translate-y-1 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +1 PUNKT
            </button>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-2xl p-6 shadow-lg">
            <div className="text-xs font-bold text-blue-700 uppercase mb-2">{awayName}</div>
            <div className="text-6xl font-black text-blue-900 mb-4">{currentAway}</div>
            <button
              disabled={busy || !canScore}
              onClick={handleAddPointAway}
              title={!canScore ? 'Mecz zakończony' : `Dodaj punkt: ${awayName}`}
              className="w-full py-6 bg-gradient-to-r from-blue-400 to-cyan-400 text-white rounded-xl font-black text-2xl hover:shadow-xl hover:shadow-blue-500/30 transition-all transform hover:-translate-y-1 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +1 PUNKT
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <button
            disabled={busy || !canScore}
            onClick={handleUndo}
            className="py-5 bg-white border-2 border-slate-300 text-slate-900 rounded-xl font-black text-lg hover:bg-slate-50 hover:border-slate-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-5 h-5" />
            COFNIJ PUNKT
          </button>

          <button
            disabled={busy || !canEndSet}
            onClick={handleEndSet}
            title={
              !canEndSet && canScore
                ? `Set musi być wygrany do co najmniej ${currentSetTarget} punktów (obecnie ${currentHome}:${currentAway}).`
                : 'Ręcznie zakończ bieżący set'
            }
            className="py-5 bg-gradient-to-r from-orange-100 to-amber-100 border-2 border-orange-300 text-orange-900 rounded-xl font-black text-lg hover:from-orange-200 hover:to-amber-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Flag className="w-5 h-5" />
            ZAKOŃCZ SET
          </button>

          <button
            disabled={busy || !canFinish}
            onClick={handleFinishMatch}
            className="py-5 bg-gradient-to-r from-[#0066FF] to-[#00C7B1] text-white rounded-xl font-black text-lg hover:shadow-xl hover:shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Check className="w-5 h-5" />
            ZAKOŃCZ MECZ
          </button>
        </div>

        {canScore &&
          currentSetMaxPts > 0 &&
          currentSetMaxPts < currentSetTarget && (
            <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl text-amber-900 text-sm font-semibold mb-4">
              Set {currentSetNo}
              {isCurrentSetTiebreak ? ' (tie-break)' : ''} musi być wygrany do co najmniej{' '}
              {currentSetTarget} punktów (obecnie {currentHome}:{currentAway}).
            </div>
          )}

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <button
            disabled={busy || !canFinish}
            onClick={() => handleWalkover('HOME')}
            title="Walkower dla HOME"
            className="py-5 bg-gradient-to-r from-red-100 to-pink-100 border-2 border-red-300 text-red-900 rounded-xl font-black text-lg hover:from-red-200 hover:to-pink-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            WALKOWER: {homeName}
          </button>

          <button
            disabled={busy || !canFinish}
            onClick={() => handleWalkover('AWAY')}
            title="Walkower dla AWAY"
            className="py-5 bg-gradient-to-r from-red-100 to-pink-100 border-2 border-red-300 text-red-900 rounded-xl font-black text-lg hover:from-red-200 hover:to-pink-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            WALKOWER: {awayName}
          </button>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-black text-slate-900 text-lg mb-4">Sety</h3>
          <div className="space-y-3">
            {sets.map((s) => (
              <div
                key={s.setNumber}
                className={`flex justify-between items-center p-4 rounded-xl border-2 transition-all ${
                  s.finishedAt
                    ? 'bg-slate-100 border-slate-300'
                    : 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200'
                }`}
              >
                <div className="font-black text-slate-900">
                  Set {s.setNumber} {s.isTiebreak ? '(TB)' : ''}
                  {s.finishedAt && s.winnerTeamId && (
                    <span className="ml-2 text-xs text-green-600">
                      ✓ {s.winnerTeamId === 'home' ? homeName : awayName}
                    </span>
                  )}
                </div>
                <div className="text-2xl font-black text-slate-900">
                  {s.homePoints}:{s.awayPoints}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl text-blue-900 text-sm">
          <p className="font-semibold mb-2">💡 Jak korzystać z panelu (DEMO):</p>
          <ul className="list-disc list-inside space-y-1 text-xs">
            <li>
              Kliknij <strong>+1 PUNKT</strong>, aby dodać punkt drużynie (działa lokalnie)
            </li>
            <li>
              <strong>COFNIJ PUNKT</strong> – cofa ostatnio dodany punkt
            </li>
            <li>
              <strong>ZAKOŃCZ SET</strong> – kończy aktualny set (min. 25 pkt, różnica 2 pkt)
            </li>
            <li>
              <strong>ZAKOŃCZ MECZ</strong> – oficjalnie kończy mecz
            </li>
            <li>
              <strong>WALKOWER</strong> – przyznaje walkower jednej z drużyn
            </li>
            <li>
              W panelu na żywo: wyniki na żywo w widoku publicznym i u organizatora.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
